import React, { useEffect, useRef, useState } from "react";
import { Globe, ChevronDown, Check } from "lucide-react";

/**
 * LanguageSwitcher — Google Translate powered language selector.
 *
 * Uses the free Google Translate Website element with a *custom* UI:
 *   - We render a compact pill "EN ▾" in the utility strip.
 *   - Clicking opens a searchable dropdown of major world languages.
 *   - Selecting a language sets the `googtrans` cookie and reloads Google
 *     Translate's hidden <select>, which drives the actual translation.
 *   - The default Google Translate banner/frame is hidden with global CSS
 *     (see /app/frontend/src/index.css `.skiptranslate`, `.goog-*`).
 *
 * Works on every public page without changing route logic.
 */
const LANGUAGES = [
  { code: "en",       label: "English",              flag: "🇬🇧" },
  { code: "hi",       label: "हिन्दी (Hindi)",        flag: "🇮🇳" },
  { code: "zh-CN",    label: "简体中文 (Chinese)",     flag: "🇨🇳" },
  { code: "zh-TW",    label: "繁體中文 (Trad.)",       flag: "🇹🇼" },
  { code: "ar",       label: "العربية (Arabic)",     flag: "🇸🇦" },
  { code: "es",       label: "Español (Spanish)",     flag: "🇪🇸" },
  { code: "fr",       label: "Français (French)",     flag: "🇫🇷" },
  { code: "de",       label: "Deutsch (German)",      flag: "🇩🇪" },
  { code: "pt",       label: "Português",             flag: "🇵🇹" },
  { code: "it",       label: "Italiano",              flag: "🇮🇹" },
  { code: "ru",       label: "Русский (Russian)",     flag: "🇷🇺" },
  { code: "ja",       label: "日本語 (Japanese)",      flag: "🇯🇵" },
  { code: "ko",       label: "한국어 (Korean)",         flag: "🇰🇷" },
  { code: "bn",       label: "বাংলা (Bengali)",       flag: "🇧🇩" },
  { code: "ur",       label: "اردو (Urdu)",           flag: "🇵🇰" },
  { code: "ta",       label: "தமிழ் (Tamil)",         flag: "🇮🇳" },
  { code: "te",       label: "తెలుగు (Telugu)",       flag: "🇮🇳" },
  { code: "mr",       label: "मराठी (Marathi)",       flag: "🇮🇳" },
  { code: "gu",       label: "ગુજરાતી (Gujarati)",   flag: "🇮🇳" },
  { code: "pa",       label: "ਪੰਜਾਬੀ (Punjabi)",     flag: "🇮🇳" },
  { code: "tr",       label: "Türkçe (Turkish)",      flag: "🇹🇷" },
  { code: "id",       label: "Bahasa Indonesia",      flag: "🇮🇩" },
  { code: "vi",       label: "Tiếng Việt (Vietnamese)", flag: "🇻🇳" },
  { code: "th",       label: "ไทย (Thai)",            flag: "🇹🇭" },
  { code: "nl",       label: "Nederlands (Dutch)",    flag: "🇳🇱" },
  { code: "pl",       label: "Polski (Polish)",       flag: "🇵🇱" },
  { code: "uk",       label: "Українська (Ukrainian)", flag: "🇺🇦" },
  { code: "he",       label: "עברית (Hebrew)",        flag: "🇮🇱" },
  { code: "fa",       label: "فارسی (Persian)",       flag: "🇮🇷" },
  { code: "sw",       label: "Kiswahili (Swahili)",   flag: "🇰🇪" },
];

const COOKIE_KEY = "googtrans";

function readCurrentLangFromCookie() {
  if (typeof document === "undefined") return "en";
  const m = document.cookie.match(/googtrans=([^;]+)/);
  if (!m) return "en";
  // googtrans cookie format is "/en/xx"
  const parts = decodeURIComponent(m[1]).split("/");
  return parts[2] || "en";
}

function setLangCookie(langCode) {
  const value = `/en/${langCode}`;
  // Set on current host, current path, and root domain to cover Cloudflare edge
  document.cookie = `${COOKIE_KEY}=${value};path=/`;
  const host = window.location.hostname;
  const parts = host.split(".");
  if (parts.length > 1) {
    const rootDomain = "." + parts.slice(-2).join(".");
    document.cookie = `${COOKIE_KEY}=${value};path=/;domain=${rootDomain}`;
  }
}

export default function LanguageSwitcher() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("en");
  const [query, setQuery] = useState("");
  const [ready, setReady] = useState(false);
  const rootRef = useRef(null);
  const hiddenRef = useRef(null);

  // Inject Google Translate element script exactly once
  useEffect(() => {
    if (typeof window === "undefined") return;
    setCurrent(readCurrentLangFromCookie());

    // Skip if already initialised
    if (window.googleTranslateElementInit) {
      setReady(true);
      return;
    }

    window.googleTranslateElementInit = function () {
      // eslint-disable-next-line no-undef
      new google.translate.TranslateElement(
        {
          pageLanguage: "en",
          autoDisplay: false,
          includedLanguages: LANGUAGES.map((l) => l.code).join(","),
          layout: window.google?.translate?.TranslateElement?.InlineLayout?.SIMPLE,
        },
        "hidden-google-translate-element"
      );
      setReady(true);
    };

    const script = document.createElement("script");
    script.src =
      "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    script.async = true;
    document.body.appendChild(script);

    // Belt & braces: Google occasionally ships new banner class names.
    // Watch the body and force-remove any translation frame it inserts.
    const killBanner = () => {
      document
        .querySelectorAll(
          "iframe.skiptranslate, iframe.goog-te-banner-frame, " +
          ".VIpgJd-ZVi9od-ORHb-OEVmcd, .VIpgJd-ZVi9od-ORHb"
        )
        .forEach((el) => { el.style.display = "none"; el.style.height = "0"; });
      if (document.body.style.top) document.body.style.top = "0px";
    };
    const observer = new MutationObserver(killBanner);
    observer.observe(document.body, { childList: true, subtree: false });
    return () => observer.disconnect();
  }, []);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const changeLang = (code) => {
    setLangCookie(code);
    setCurrent(code);
    setOpen(false);
    // Trigger Google's hidden <select> if available, otherwise hard reload
    const select = document.querySelector(".goog-te-combo");
    if (select) {
      select.value = code;
      select.dispatchEvent(new Event("change"));
    } else {
      window.location.reload();
    }
  };

  const filtered = LANGUAGES.filter((l) =>
    l.label.toLowerCase().includes(query.toLowerCase()) ||
    l.code.toLowerCase().includes(query.toLowerCase())
  );
  const activeLang = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];

  return (
    <div ref={rootRef} className="relative" data-testid="lang-switcher">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        data-testid="lang-switcher-btn"
        aria-label="Change language"
        aria-expanded={open}
        aria-haspopup="listbox"
        className="flex items-center gap-1.5 px-2.5 py-1 border border-white/20 hover:border-[#B8965A] text-white text-[10px] uppercase tracking-[0.14em] transition-colors"
      >
        <Globe size={11} />
        <span className="notranslate">{activeLang.code.toUpperCase()}</span>
        <ChevronDown size={10} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 mt-1 w-72 max-h-[70vh] overflow-hidden bg-white text-[#12283F] border border-[#E2E8F0] shadow-xl z-[100]"
          data-testid="lang-switcher-menu"
        >
          <div className="p-2 border-b border-[#E2E8F0] bg-[#F8FAFC]">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search language…"
              className="w-full border border-[#E2E8F0] px-2 py-1.5 text-xs"
              data-testid="lang-search"
            />
          </div>
          <div className="max-h-[52vh] overflow-y-auto py-1">
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-center text-xs text-[#6B7280]">
                No language found
              </div>
            )}
            {filtered.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => changeLang(l.code)}
                data-testid={`lang-option-${l.code}`}
                className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-[#FBF7EE] transition-colors notranslate ${
                  current === l.code ? "bg-[#FBF7EE] font-semibold" : ""
                }`}
              >
                <span className="text-base leading-none">{l.flag}</span>
                <span className="flex-1">{l.label}</span>
                {current === l.code && <Check size={12} className="text-[#B8965A]" />}
              </button>
            ))}
          </div>
          <div className="px-3 py-2 border-t border-[#E2E8F0] bg-[#F8FAFC] text-[9px] uppercase tracking-widest text-[#6B7280]">
            Powered by Google Translate
          </div>
        </div>
      )}

      {/* Hidden Google Translate element — controlled programmatically */}
      <div
        id="hidden-google-translate-element"
        ref={hiddenRef}
        style={{ display: "none" }}
        aria-hidden="true"
      />
    </div>
  );
}
